import { Component } from '@theme/component';
import { lockScroll, unlockScroll, isClickedOutside } from '@theme/utilities';

const SUBMITTED_KEY = 'sd-newsletter-form';

/**
 * SD: EDARA Footer.
 * - Newsletter-Popup (<dialog>) öffnen/schließen, nach dem Absenden mit Meldung wieder öffnen
 * - "Cookie-Einstellungen" öffnet Shopifys Cookie-Banner
 * - "Client Service" öffnet das Client-Service-Popup aus dem Header
 * - Land/Sprache-Auswahl schließt bei Klick daneben
 *
 * @typedef {{ newsletterDialog: HTMLDialogElement }} Refs
 * @extends {Component<Refs>}
 */
class SdFooter extends Component {
  requiredRefs = ['newsletterDialog'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('submit', this.#onSubmit);
    document.addEventListener('click', this.#closeDropdowns);

    const dialog = this.refs.newsletterDialog;
    if (dialog.querySelector('[data-form-message]') && consumeSubmitted(dialog.querySelector('form')?.id)) {
      this.openNewsletter();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('submit', this.#onSubmit);
    document.removeEventListener('click', this.#closeDropdowns);
  }

  openNewsletter() {
    const dialog = this.refs.newsletterDialog;
    dialog.showModal();
    lockScroll(dialog);
    dialog.addEventListener('close', () => unlockScroll(dialog), { once: true });
  }

  closeNewsletter() {
    this.refs.newsletterDialog.close();
  }

  /**
   * Klick auf den abgedunkelten Hintergrund schließt das Popup.
   * Per Klickposition prüfen: Horizons on:click setzt event.target immer auf den Dialog.
   * @param {MouseEvent} event
   */
  onDialogClick(event) {
    if (isClickedOutside(event, this.refs.newsletterDialog)) this.closeNewsletter();
  }

  /** Öffnet das Client-Service-Popup aus dem Header (sd-header.js). */
  openClientService() {
    const header = /** @type {HTMLElement & { openService?: () => void } | null} */ (document.querySelector('sd-header'));
    header?.openService?.();
  }

  openCookieSettings() {
    // @ts-ignore – wird von Shopifys Cookie-Banner bereitgestellt
    window.privacyBanner?.showPreferences();
  }

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    if (event.target instanceof HTMLFormElement) rememberSubmitted(event.target.id);
  };

  /** @param {MouseEvent} event */
  #closeDropdowns = (event) => {
    for (const details of this.querySelectorAll('details[open]')) {
      if (!details.contains(/** @type {Node} */ (event.target))) details.removeAttribute('open');
    }
  };
}

/** @param {string} id */
function rememberSubmitted(id) {
  try {
    sessionStorage.setItem(SUBMITTED_KEY, id);
  } catch {}
}

/**
 * Prüft, ob dieses Formular zuletzt abgeschickt wurde, und löscht den Eintrag (gleiche Logik wie sd-header.js).
 * @param {string | undefined} id
 */
function consumeSubmitted(id) {
  try {
    if (!id || sessionStorage.getItem(SUBMITTED_KEY) !== id) return false;
    sessionStorage.removeItem(SUBMITTED_KEY);
    return true;
  } catch {
    return false;
  }
}

if (!customElements.get('sd-footer')) {
  customElements.define('sd-footer', SdFooter);
}
